import {isEmpty} from "./utils.js";

export class TimeDelta {
    constructor(years, months, days, hours, minutes, seconds) {
        this.years = years || 0;
        this.months = months || 0;
        this.days = days || 0;
        this.hours = hours || 0;
        this.minutes = minutes || 0;
        this.seconds = seconds || 0;
    }

    static getDelta(date1, date2) {
        return new TimeDelta(
            date1.getFullYear() - date2.getFullYear(),
            date1.getMonth() - date2.getMonth(),
            date1.getDate() - date2.getDate(),
            date1.getHours() - date2.getHours(),
            date1.getMinutes() - date2.getMinutes(),
            date1.getSeconds() - date2.getSeconds()
        )
    }

    static getHalfwayBetween({startDate, endDate}) {
        /*
        Returns a date that is halfway between startDate and endDate.
         */
        if (startDate > endDate) {
            throw new Error("startDate must be before endDate");
        }
        return new Date((startDate.getTime() + endDate.getTime()) / 2);
    }

    totalMilliseconds() {
        return this.seconds * 1000 + this.minutes * 60 * 1000 + this.hours * 60 * 60 * 1000 + this.days * 24 * 60 * 60 * 1000 + this.months * 30 * 24 * 60 * 60 * 1000 + this.years * 365 * 24 * 60 * 60 * 1000;
    }

    static incrementByMilliseconds(date, milliseconds) {
        return new Date(date.getTime() + milliseconds);
    }

    static floorToDay(date) {
        if (isEmpty(date)) {
            throw new Error("Input date cannot be empty or undefined: " + date);
        }
        if (typeof date === "string") {
            date = new Date(date);
            if (isNaN(date.getTime())) {
                throw new Error("Invalid date string: " + date);
            }
        }
        if (date instanceof Date) {
            const copy = new Date(date.getTime())
            copy.setHours(0);
            copy.setMinutes(0);
            copy.setSeconds(0);
            copy.setMilliseconds(0);
            return copy;
        }
        throw new Error("Input must be a Date object: " + date + ": " + typeof date);
    }

    increment(date) {
        const incremented = new Date(date);
        incremented.setFullYear(date.getFullYear() + this.years);
        incremented.setMonth(date.getMonth() + this.months);
        incremented.setDate(date.getDate() + this.days);
        incremented.setHours(date.getHours() + this.hours);
        incremented.setMinutes(date.getMinutes() + this.minutes);
        incremented.setSeconds(date.getSeconds() + this.seconds);
        return incremented;
    }

    divideBy(value) {
        return new TimeDelta(
            this.years / value, this.months / value, this.days / value,
            this.hours / value, this.minutes / value, this.seconds / value
        )
    }

    addTo(date) {
        return this.increment(date);
    }

    subtractFrom(date) {
        const negated = new TimeDelta(
            -this.years, -this.months, -this.days, -this.hours, -this.minutes, -this.seconds
        );
        return negated.increment(date);
    }

    static padWindow(dateWindow) {
        return this.leftPad(this.rightPad(dateWindow));
    }

    static leftPad (dateWindow) {
        while(dateWindow[0].getUTCDay() !== 0) {
            const previousDay = new Date(dateWindow[0].getTime());
            previousDay.setUTCDate(previousDay.getUTCDate() - 1);
            dateWindow = [previousDay, ...dateWindow];
        }
        return dateWindow;
    }

    static rightPad(dateWindow) {
        while (dateWindow[dateWindow.length - 1].getUTCDay() !== 6) {
            const nextDay = new Date(dateWindow[dateWindow.length - 1].getTime());
            nextDay.setUTCDate(nextDay.getUTCDate() + 1);
            dateWindow = [...dateWindow, nextDay];
        }
        return dateWindow;
    }

    static alignWindows(firstWindow, secondWindow) {
        /*
        This method aligns firstWindow and secondWindow so they both begin on a Sunday and end on a Saturday with the same
        number of days and no gaps between days.

        @param Array[Date, Date] firstWindow: An array of two date object indicating the start and end of the first window.
        @param Array[Date, Date] secondWindow: An array of two date object indicating the start and end of the second window.
         */
        let firstDates = TimeDelta.daysFrom(
            firstWindow.start,
            firstWindow.end
        ).sort((a, b) => a - b);

        let secondDates = TimeDelta.daysFrom(
            secondWindow.start,
            secondWindow.end
        ).sort((a, b) => a - b);


        // Calculate how we will need to add the least number of total nulls to align the windows.
        if (firstDates.length === 0 || secondDates.length === 0) {
            return [[], []];
        }
        const [firstStartDay, secondStartDay] = [firstDates[0].getDay(), secondDates[0].getDay()];

        if (firstStartDay < secondStartDay) {
            const nullsToAdd = (secondStartDay - firstStartDay);
            secondDates = Array(nullsToAdd).fill(null).concat(secondDates);
        } else if (firstStartDay > secondStartDay) {
            const nullsToAdd = (firstStartDay - secondStartDay);
            firstDates = Array(nullsToAdd).fill(null).concat(firstDates);
        }

            // Now both windows start on the same day of the week, just pad the shorter with nulls.
        if (firstDates.length > secondDates.length) {
            const nullsToAdd = firstDates.length - secondDates.length;
            secondDates = secondDates.concat(Array(nullsToAdd).fill(null));
        } else if (secondDates.length > firstDates.length) {
            const nullsToAdd = secondDates.length - firstDates.length;
            firstDates = firstDates.concat(Array(nullsToAdd).fill(null));
        }
        return [firstDates, secondDates];
    }

    static formatShortDate(date) {
        const month = date.getMonth() + 1;
        const day = date.getDate();
        return `${month}/${day}`;
    }

    static daysFrom(startDate, endDate) {
        /*
        Returns days between startDate and endDate inclusive, with hour, minute, second set to 0.
         */
        const start = new Date(startDate.getTime());
        const end = new Date(endDate.getTime());
        const days = [];
        let current = TimeDelta.floorToDay(start);
        while (current <= end) {
            days.push(new Date(current));
            current.setDate(current.getDate() + 1);
        }
        return days;
    }

    static getRanges(startDate, endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const yearDelta = new TimeDelta(1, 0, 0, 0, 0, 0);
        const monthDelta = new TimeDelta(0, 1, 0, 0, 0, 0);
        const weekDelta = new TimeDelta(0, 0, 7, 0, 0, 0);
        const dayDelta = new TimeDelta(0, 0, 1, 0, 0, 0);
        const ranges = [];
        let currentIncrement = yearDelta;
        const increments = [yearDelta, monthDelta, weekDelta, dayDelta];
        let current = start;
        while (current < end) {
            const next = currentIncrement.increment(current);
            if (next > end) {
                if (increments.length === 0) {
                    if(current !== end) {
                        ranges.push([current, end]);
                    }
                    return ranges;
                } else {
                    currentIncrement = increments.shift();
                    continue;
                }
            }
            ranges.push([current, next]);
            current = next;
        }
        return ranges;
    }

    static isToday(inputDate) {
        const today = new Date();
        return inputDate.getUTCFullYear() === today.getUTCFullYear() &&
            inputDate.getUTCMonth() === today.getUTCMonth() &&
            inputDate.getUTCDate() === today.getUTCDate();
    }

    static isThisWeek(inputDate) {
        const today = new Date();
        const dateWeekStart = TimeDelta.getSunday(inputDate);
        const todayWeekStart = TimeDelta.getSunday(today);
        return dateWeekStart.getTime() === todayWeekStart.getTime();
    }

    static isThisMonth(inputDate) {
        const today = new Date();
        return inputDate.getUTCFullYear() === today.getUTCFullYear() &&
            inputDate.getUTCMonth() === today.getUTCMonth();
    }

    static isThisYear(inputDate) {
        const today = new Date();
        return inputDate.getUTCFullYear() === today.getUTCFullYear();
    }

    static getCanonicalFloor(inputDate) {
        /*
        The canonical floor for a date is
          1) The first day of the year if the year is not the current calendar year
          2) The first day of the month if the month is not the current calendar month or the year is not the current calendar year
          3) The first day of the week if the week is not the current calendar week.
          4) The first hour/minute/second of the day if the day is not the current day and that day is less than a
             week ago.
         */
        if (!inputDate || !(inputDate instanceof Date)) {
            if (inputDate.$isDayjsObject) {
                inputDate = inputDate.$d;
            }
        }
        const localYear = inputDate.getFullYear();
        const localMonth = inputDate.getMonth();
        const localDate = inputDate.getDate();

        const localDateObject = new Date(localYear, localMonth, localDate);
        const localNow = new Date();

        // Same Day? Return the start of the day.
        if (this.isToday(localDateObject)) {
            return new Date(localDateObject.getFullYear(), localDateObject.getMonth(), localDateObject.getDate());
        }
        // Same Week? Return the start of the week.
        if (this.isThisWeek(localDateObject)) {
            return TimeDelta.getSunday(localDateObject);
        }
        // Same month?  Return the start of the week it belongs to.
        if (this.isThisMonth(localDateObject)) {
            return TimeDelta.getSunday(localDateObject); // first of week
        }
        // Same year? Return the first of the month it belongs to.
        // if (this.isThisYear(localDateObject)) {
            return new Date(localDateObject.getFullYear(), localDateObject.getMonth(), 1);
        // }
        // Date is in a different year? Return the first of that year.
        // return new Date(localDateObject.getFullYear(), 0, 1);
    }

    static getCanonicalRanges(startDate, endDate) {
        const start = this.getCanonicalFloor(startDate);
        return this.getRanges(start, endDate);
    }

    static getSunday(date) {
        const inputDate = new Date(date.getTime()); // Make a copy to avoid mutating the original
        return new Date(inputDate.getFullYear(), inputDate.getMonth(), inputDate.getDate() - inputDate.getDay());
    }

};